import {
  isDraftClaimedBySurface,
  withoutDraftSource,
} from './shared-responder-v2-core-draft-source.helpers';
import {
  resolvePickedFollowUpPrompt,
  selectPendingFollowUp,
  toFollowUpSpec,
} from './shared-responder-v2-core-follow-up-prompt.helpers';
import {
  hasUnansweredFollowUp,
  makeFollowUpAnswerTurn,
} from './shared-responder-v2-core-session-turns.helpers';
import type { ResponderSessionState } from './shared-responder-v2-core-session.types';
import type { ResponderDraftEvent } from './shared-responder-v2-core-thread-behaviour';
import {
  isChoiceDraftComplete,
  reduceChoiceDraft,
} from './shared-responder-v2-core-thread-choice';
import {
  isOpenEndedDraftComplete,
  reduceOpenEndedDraft,
} from './shared-responder-v2-core-thread-open-ended';
import { getThreadBehaviour } from './shared-responder-v2-core-thread-registry';
import {
  DRAFT_SOURCES,
  THREAD_STATUSES,
  type DraftSource,
} from './shared-responder-v2-core-thread.consts';
import type {
  QuestionSpec,
  ResponderAnswer,
  ResponderThread,
} from './shared-responder-v2-core-thread.types';

/**
 * Whether what the responder types next is a reply to an AI follow-up, not the question's own
 * answer — the one condition shared by the composer (`selectHasActiveFollowUp`), the reducer,
 * and submit, so read and write agree. Editing a question wins over its open follow-up,
 * discarding the reply. "Open" is `hasUnansweredFollowUp` (the same backwards scan as the
 * advance decision), not "last turn is the follow-up": the backend appends a `replyFeedback`
 * bubble behind the follow-up, so a last-turn test misroutes replies on quant kinds and
 * double-submits the primary answer.
 */
export function isReplyingToFollowUp(
  state: ResponderSessionState,
  questionId: string
): boolean {
  if (
    state.editing.mode === 'editing' &&
    state.editing.questionId === questionId
  ) {
    return false;
  }

  const thread = state.threads[questionId];

  return thread !== undefined && hasUnansweredFollowUp(thread);
}

/**
 * The draft the responder is editing: the question's own answer, or the follow-up reply.
 * While a follow-up is open, the *open-ended* behaviour owns the draft instead of the kind's
 * own, since a quant reducer can't handle `textChanged` — routing every reader/writer through
 * here applies that uniformly across all kinds.
 */
export function resolveActiveDraft(
  state: ResponderSessionState,
  questionId: string
): ResponderAnswer | null {
  const thread = state.threads[questionId];

  if (thread === undefined) {
    return null;
  }

  return isReplyingToFollowUp(state, questionId)
    ? thread.followUpDraft
    : thread.draft;
}

/**
 * The spec the responder is answering right now: the question's own, or the one a pending
 * backend follow-up describes. A follow-up carries its options on the turn, so every reader
 * that reasons about options — spoken or typed — has to overlay them before it reads `spec`,
 * or a Task, which has none of its own, looks unanswerable.
 */
export function resolveActiveSpec(
  state: ResponderSessionState,
  questionId: string
): QuestionSpec | null {
  const spec = state.specs[questionId];
  const thread = state.threads[questionId];

  if (spec === undefined || thread === undefined) {
    return null;
  }

  const prompt = isReplyingToFollowUp(state, questionId)
    ? resolvePickedFollowUpPrompt(thread.kind, selectPendingFollowUp(thread))
    : null;

  return prompt === null ? spec : toFollowUpSpec(prompt, spec);
}

/**
 * Whether the thread's own pick is frozen: answered, the kind answers on the question surface
 * (not the composer), and the responder isn't editing this question — otherwise a stray
 * drag/tap on a ranking or rating surface could rewrite a pick a follow-up was already
 * generated from. Excludes `answersInComposer` kinds (open-ended, contact info), which have no
 * separate pick to freeze, and `Failed` threads, since freezing a rejected pick (e.g. a
 * `selectionBoundsViolated`) would brick the only control that could resend it. Not consulted
 * on the follow-up-reply path in `applyDraftEdit`, since `isReplyingToFollowUp` already routes
 * around it and the reply must stay typable while the pick is frozen.
 */
export function isThreadAnswerLocked(
  state: ResponderSessionState,
  questionId: string
): boolean {
  const thread = state.threads[questionId];

  if (thread === undefined || thread.answer === null) {
    return false;
  }

  if (
    thread.status === THREAD_STATUSES.Failed ||
    getThreadBehaviour(thread.kind).answersInComposer
  ) {
    return false;
  }

  return !(
    state.editing.mode === 'editing' && state.editing.questionId === questionId
  );
}

/**
 * Whether the backend closed this question with nothing further to ask; any submission is
 * refused, since `addQuestionMessage` is append-only and errors ("Question session already
 * completed") on it. `isThreadAnswerLocked` doesn't cover this, since it returns false for
 * `answersInComposer` kinds — exactly the ones that keep the input alive after completion. An
 * open follow-up is not settled, since the assistant is still asking and the reply must stay sendable.
 */
export function isThreadSettled(
  state: ResponderSessionState,
  questionId: string
): boolean {
  const thread = state.threads[questionId];

  if (thread === undefined || thread.status !== THREAD_STATUSES.Closed) {
    return false;
  }

  return !isReplyingToFollowUp(state, questionId);
}

export function isFollowUpPickedOnCard(thread: ResponderThread): boolean {
  return (
    resolvePickedFollowUpPrompt(thread.kind, selectPendingFollowUp(thread)) !==
    null
  );
}

export function isActiveDraftComplete(
  state: ResponderSessionState,
  questionId: string
): boolean {
  const spec = state.specs[questionId];
  const thread = state.threads[questionId];

  if (spec === undefined || thread === undefined) {
    return false;
  }

  if (isReplyingToFollowUp(state, questionId)) {
    const prompt = resolvePickedFollowUpPrompt(
      thread.kind,
      selectPendingFollowUp(thread)
    );

    return prompt !== null
      ? isChoiceDraftComplete(
          thread.followUpDraft,
          toFollowUpSpec(prompt, spec)
        )
      : isOpenEndedDraftComplete(thread.followUpDraft);
  }

  return getThreadBehaviour(thread.kind).isDraftComplete(thread.draft, spec);
}

function resolveDraftSource(event: ResponderDraftEvent): DraftSource {
  return event.type === 'spokenPick'
    ? DRAFT_SOURCES.Spoken
    : DRAFT_SOURCES.Surface;
}

/**
 * An unchanged draft reference is this kernel's convention for "event not recognized" (every
 * `reduceDraft` returns its input verbatim on a non-match); reusing that signal instead of a
 * hardcoded "open-ended" event list stops a new event type from being silently dropped.
 * A kind reducer that DOES recognize the event still gets dropped unless it carries `text` or
 * `transcript`, since re-running a pick reducer mid-follow-up would silently rewrite an
 * already-submitted answer.
 */
function reduceFollowUpDraft(
  thread: ResponderThread,
  event: ResponderDraftEvent,
  spec: QuestionSpec
): ResponderThread {
  const prompt = resolvePickedFollowUpPrompt(
    thread.kind,
    selectPendingFollowUp(thread)
  );
  const followUpDraft =
    prompt !== null
      ? reduceChoiceDraft(
          thread.followUpDraft,
          event,
          toFollowUpSpec(prompt, spec)
        )
      : reduceOpenEndedDraft(thread.followUpDraft, event);

  if (followUpDraft !== thread.followUpDraft) {
    return {
      ...thread,
      followUpDraft,
      draftSource: resolveDraftSource(event),
    };
  }

  const draft = getThreadBehaviour(thread.kind).reduceDraft(
    thread.draft,
    event,
    spec
  );

  if (draft === thread.draft) {
    if (process.env['NODE_ENV'] !== 'production') {
      console.warn(
        `Draft event "${event.type}" was not handled by the open-ended reducer or the "${thread.kind}" reducer.`
      );
    }

    return thread;
  }

  if (!('text' in event) && !('transcript' in event)) {
    return thread;
  }

  return { ...thread, draft };
}

export function applyDraftEdit(
  state: ResponderSessionState,
  questionId: string,
  event: ResponderDraftEvent
): ResponderSessionState {
  const spec = state.specs[questionId];
  const thread = state.threads[questionId];

  if (spec === undefined || thread === undefined) {
    return state;
  }

  if (event.type === 'spokenPick' && isDraftClaimedBySurface(thread)) {
    return state;
  }

  if (isReplyingToFollowUp(state, questionId)) {
    return {
      ...state,
      threads: {
        ...state.threads,
        [questionId]: reduceFollowUpDraft(thread, event, spec),
      },
    };
  }

  if (isThreadAnswerLocked(state, questionId)) {
    return state;
  }

  const draft = getThreadBehaviour(thread.kind).reduceDraft(
    thread.draft,
    event,
    spec
  );
  const edited =
    draft === thread.draft
      ? { ...thread, draft }
      : { ...thread, draft, draftSource: resolveDraftSource(event) };

  return { ...state, threads: { ...state.threads, [questionId]: edited } };
}

/**
 * Commits a follow-up reply: bubble, outbound payload and emptied slot in one change,
 * mirroring `appendResponderTurn`. Overwrites `answer` with the reply (not the pick), since
 * `onRetryTurn` re-issues `answer` on failure; the pick survives in `draft` for the question
 * surfaces to read.
 */
export function commitFollowUpReply(
  thread: ResponderThread,
  reply: ResponderAnswer
): ResponderThread {
  const prompt = selectPendingFollowUp(thread)?.prompt ?? null;
  const turn = makeFollowUpAnswerTurn(
    thread.questionId,
    reply,
    prompt,
    thread.turns.length
  );

  if (turn === null) {
    return thread;
  }

  return {
    ...withoutDraftSource(thread),
    followUpDraft: null,
    answer: reply,
    status: THREAD_STATUSES.AwaitingAssistant,
    requestSeq: thread.requestSeq + 1,
    turns: [...thread.turns, turn],
  };
}
